export interface ClinicalComment {
  id: string;
  date: string;
  author: string;
  content: string;
  type: 'note' | 'observation' | 'alert';
}

export const CLINICAL_COMMENTS: ClinicalComment[] = [
  {
    id: 'c1',
    date: '2024-11-15',
    author: 'Dr. Moreau',
    content:
      'Bonne coopération. Port des élastiques conforme (>16 h/jour selon journal de bord). Progression satisfaisante de la correction sagittale.',
    type: 'note',
  },
  {
    id: 'c2',
    date: '2024-10-22',
    author: 'Dr. Moreau',
    content:
      'Légère inflammation gingivale au niveau du secteur antérieur inférieur. Rappels hygiène bucco-dentaire renforcés. Contrôle dans 4 semaines.',
    type: 'observation',
  },
  {
    id: 'c3',
    date: '2024-09-10',
    author: 'Dr. Moreau',
    content:
      'ATTENTION : allergie au latex confirmée par allergologue. Utiliser exclusivement des élastiques et matériaux sans latex.',
    type: 'alert',
  },
  {
    id: 'c4',
    date: '2024-07-22',
    author: 'Dr. Moreau',
    content:
      "Changement d'arc : passage en 0.16×0.22 Niti. Activation des élastiques classe II (50g/côté). Superposition 3D T0/T6 favorable.",
    type: 'note',
  },
  {
    id: 'c5',
    date: '2024-05-14',
    author: 'Dr. Moreau',
    content:
      'Retrait du disjoncteur. Expansion obtenue satisfaisante. Espace de contention maintenu. Collage brackets maxillaire et mandibulaire.',
    type: 'observation',
  },
];

export interface DiagnosticData {
  classification: string;
  skeletalRelation: string;
  dentalRelation: string;
  crowding: string;
  verticalDimension: string;
  transverseRelation: string;
  notes: string;
}

export const DIAGNOSTIC: DiagnosticData = {
  classification: 'Classe II division 1',
  skeletalRelation:
    'Décalage sagittal squelettique modéré (ANB = 5°, Wits = +4 mm)',
  dentalRelation: 'Surplomb incisif augmenté 8 mm, supraclusion 30 %',
  crowding:
    'Encombrement modéré au maxillaire (−4 mm), léger à la mandibule (−2 mm)',
  verticalDimension: 'Normodivergent (FMA = 26°, SN/GoGn = 32°)',
  transverseRelation:
    'Endognathie maxillaire légère avec latérodéviation mandibulaire fonctionnelle',
  notes:
    'Respiration buccale à corriger (bilan ORL conseillé). Ventilation nasale à rééduquer. Classe II squelettique à correction orthodontico-orthoédique.',
};

export interface TreatmentStep {
  id: string;
  phase: string;
  description: string;
  duration: string;
  status: 'done' | 'current' | 'upcoming';
  startDate?: string;
  endDate?: string;
}

/** Hash djb2 + finalizer "avalanche" type xxhash — fallback si l'uuid n'est
 *  pas au format attendu (parsePatientN renvoie -1). */
function hashNum(s: string): number {
  let h = 5381;
  for (let i = 0; i < s.length; i++) {
    h = ((h * 33) ^ s.charCodeAt(i)) >>> 0;
  }
  h ^= h >>> 16;
  h = Math.imul(h, 0x85ebca6b) >>> 0;
  h ^= h >>> 13;
  h = Math.imul(h, 0xc2b2ae35) >>> 0;
  h ^= h >>> 16;
  return h >>> 0;
}

/** Extrait l'index numérique encodé dans la queue de l'uuid mock. Les uuids
 *  patient suivent le format `a0000000-0000-4000-8000-{12 hex}` où le suffixe
 *  hex encode `practiceIndex * 1000 + patientIndex`. */
function parsePatientN(uuid: string): number {
  const m = /-([0-9a-f]{12})$/i.exec(uuid);
  if (!m || !m[1]) return -1;
  // Les 10 derniers caractères hex tiennent largement dans Number.
  const n = parseInt(m[1].slice(-10), 16);
  return Number.isFinite(n) ? n : -1;
}

/** Encode l'index patient en base mixte (7, 6, 6, 6). Pour 56 patients par
 *  cabinet, toutes les combinaisons (diag, plan, obj, com) sont distinctes
 *  (la base totale autorise 1512 combinaisons). */
function pickIndices(uuid: string): {
  diag: number;
  plan: number;
  obj: number;
  com: number;
} {
  let n = parsePatientN(uuid);
  if (n < 0) n = hashNum(uuid);
  const diag = n % 7;
  const plan = Math.floor(n / 7) % 6;
  const obj = Math.floor(n / 42) % 6;
  const com = Math.floor(n / 252) % 6;
  return { diag, plan, obj, com };
}

const DIAGNOSTIC_VARIANTS: DiagnosticData[] = [
  {
    classification: 'Classe II division 1',
    skeletalRelation:
      'Décalage sagittal squelettique modéré (ANB = 5°, Wits = +4 mm)',
    dentalRelation: 'Surplomb incisif augmenté 8 mm, supraclusion 30 %',
    crowding:
      'Encombrement modéré au maxillaire (−4 mm), léger à la mandibule (−2 mm)',
    verticalDimension: 'Normodivergent (FMA = 26°, SN/GoGn = 32°)',
    transverseRelation:
      'Endognathie maxillaire légère avec latérodéviation mandibulaire fonctionnelle',
    notes:
      'Respiration buccale à corriger (bilan ORL conseillé). Ventilation nasale à rééduquer. Classe II squelettique à correction orthodontico-orthopédique.',
  },
  {
    classification: 'Classe I avec encombrement sévère',
    skeletalRelation:
      'Rapport sagittal squelettique normal (ANB = 2°, Wits = 0 mm)',
    dentalRelation: 'Surplomb 2 mm, recouvrement 25 %',
    crowding:
      'Encombrement sévère bimaxillaire (−7 mm au maxillaire, −6 mm à la mandibule)',
    verticalDimension: 'Légèrement hypodivergent (FMA = 22°)',
    transverseRelation: 'Coordination transversale acceptable',
    notes:
      'Évaluation orthodontique pour extractions thérapeutiques. Indication potentielle de prémolaires.',
  },
  {
    classification: 'Classe III modérée',
    skeletalRelation:
      'Tendance Classe III squelettique (ANB = −1°, Wits = −3 mm)',
    dentalRelation: 'Bout-à-bout incisif, recouvrement 10 %',
    crowding: 'Encombrement léger à la mandibule (−2 mm)',
    verticalDimension: 'Normodivergent',
    transverseRelation:
      "Inversé d'occlusion postérieur droit, latérodéviation mandibulaire de 2 mm",
    notes:
      'Surveillance de la croissance mandibulaire. Camouflage orthodontique envisageable si stabilisation.',
  },
  {
    classification: 'Béance antérieure',
    skeletalRelation: 'Hyperdivergence faciale (FMA = 34°, SN/GoGn = 39°)',
    dentalRelation: 'Béance antérieure de 3 mm',
    crowding: 'Alignement maxillaire correct, encombrement léger mandibulaire',
    verticalDimension: 'Hyperdivergent',
    transverseRelation: 'Endognathie maxillaire bilatérale',
    notes:
      'Interposition linguale persistante. Rééducation linguale indispensable avant traitement actif.',
  },
  {
    classification: 'Classe II division 2',
    skeletalRelation:
      'Décalage sagittal modéré avec rotation horaire mandibulaire',
    dentalRelation:
      'Linguoversion des incisives centrales, vestibuloversion des latérales',
    crowding: 'Encombrement léger maxillaire (−2 mm)',
    verticalDimension: 'Hypodivergent (FMA = 19°)',
    transverseRelation: 'Coordination acceptable',
    notes:
      'Supraclusion marquée. Désengrenage antérieur prioritaire avant alignement.',
  },
  {
    classification: 'Promaxillie',
    skeletalRelation: 'Hypermaxillie sagittale (SNA = 86°)',
    dentalRelation: 'Vestibuloversion généralisée des incisives supérieures',
    crowding: 'Espaces inter-incisifs maxillaires',
    verticalDimension: 'Normodivergent',
    transverseRelation: 'Endoalvéolie maxillaire postérieure',
    notes:
      "Profil convexe. Discussion d'extractions pour rétraction antérieure.",
  },
  {
    classification: 'Asymétrie mandibulaire',
    skeletalRelation: 'Déviation mentonnière gauche de 4 mm, ANB normal',
    dentalRelation:
      'Décalage du milieu inter-incisif inférieur de 3 mm vers la gauche',
    crowding: 'Encombrement modéré antéro-inférieur (−4 mm)',
    verticalDimension: 'Normodivergent',
    transverseRelation:
      'Articulé croisé latéral droit, latéralité fonctionnelle',
    notes:
      'Bilan articulaire ATM recommandé. Suivi de la croissance condylienne.',
  },
];

export function diagnosticForPatient(uuid: string): DiagnosticData {
  return DIAGNOSTIC_VARIANTS[
    pickIndices(uuid).diag % DIAGNOSTIC_VARIANTS.length
  ]!;
}

export const TREATMENT_PLAN: TreatmentStep[] = [
  {
    id: 'step1',
    phase: 'Phase 1 — Expansion palatine',
    description:
      "Disjoncteur palatif vissé pour correction de l'endognathie maxillaire. Activation quotidienne par le patient.",
    duration: '4 mois',
    status: 'done',
    startDate: '2024-01-10',
    endDate: '2024-05-10',
  },
  {
    id: 'step2',
    phase: 'Phase 2 — Multi-attaches',
    description:
      'Collage brackets métal + arc Niti 0.14 initial. Levée de courbe, alignement, nivellement. Élastiques classe II intermaxillaires.',
    duration: '18 mois',
    status: 'current',
    startDate: '2024-06-01',
  },
  {
    id: 'step3',
    phase: 'Phase 3 — Finition',
    description:
      'Arcs acier SS 0.19×0.25. Élastiques de finition. Correction des détails occlusaux et fermeture des espaces résiduels.',
    duration: '6 mois',
    status: 'upcoming',
  },
  {
    id: 'step4',
    phase: 'Phase 4 — Contention',
    description:
      'Bague de contention fixe collée 3-3 mandibulaire + gouttière de contention thermoformée maxillaire. Port nocturne indéfini.',
    duration: 'Indéfinie',
    status: 'upcoming',
  },
];

const TREATMENT_PLAN_VARIANTS: TreatmentStep[][] = [
  TREATMENT_PLAN,
  [
    {
      id: 'a1',
      phase: 'Phase 1 — Extractions thérapeutiques',
      description:
        'Extraction des quatre premières prémolaires. Cicatrisation 4 semaines avant collage.',
      duration: '1 mois',
      status: 'done',
      startDate: '2024-02-05',
      endDate: '2024-03-05',
    },
    {
      id: 'a2',
      phase: 'Phase 2 — Alignement & nivellement',
      description:
        "Arcs Niti 0.14 puis 0.16×0.22. Fermeture progressive des sites d'extraction.",
      duration: '12 mois',
      status: 'current',
      startDate: '2024-03-15',
    },
    {
      id: 'a3',
      phase: 'Phase 3 — Mécanique de glissement',
      description:
        'Fermeture des espaces résiduels par chaînettes élastomériques sur arc SS 0.19×0.25.',
      duration: '8 mois',
      status: 'upcoming',
    },
    {
      id: 'a4',
      phase: 'Phase 4 — Contention',
      description: 'Fil collé bimaxillaire + gouttière nocturne.',
      duration: 'Indéfinie',
      status: 'upcoming',
    },
  ],
  [
    {
      id: 'b1',
      phase: 'Phase 1 — Disjonction maxillaire',
      description:
        'Disjoncteur Hyrax 0,5 mm/jour pendant 3 semaines. Stabilisation 6 mois.',
      duration: '6 mois',
      status: 'current',
      startDate: '2024-09-01',
    },
    {
      id: 'b2',
      phase: 'Phase 2 — Masque de Delaire',
      description: 'Traction antérieure maxillaire 14 h/jour, 400 g par côté.',
      duration: '10 mois',
      status: 'upcoming',
    },
    {
      id: 'b3',
      phase: 'Phase 3 — Multi-attaches',
      description: 'Correction des axes dentaires et finition occlusale.',
      duration: '14 mois',
      status: 'upcoming',
    },
    {
      id: 'b4',
      phase: 'Phase 4 — Contention',
      description: "Surveillance de la croissance jusqu'à fin de puberté.",
      duration: 'Indéfinie',
      status: 'upcoming',
    },
  ],
  [
    {
      id: 'c1',
      phase: 'Phase 1 — Rééducation linguale',
      description:
        'Séances avec orthophoniste, écran lingual + grille anti-langue.',
      duration: '6 mois',
      status: 'done',
      startDate: '2023-09-10',
      endDate: '2024-03-10',
    },
    {
      id: 'c2',
      phase: 'Phase 2 — Aligneurs',
      description:
        'Traitement par aligneurs transparents avec attachements composites.',
      duration: '14 mois',
      status: 'current',
      startDate: '2024-04-01',
    },
    {
      id: 'c3',
      phase: 'Phase 3 — Affinements',
      description: 'Série de raffinements pour la fermeture de la béance.',
      duration: '5 mois',
      status: 'upcoming',
    },
    {
      id: 'c4',
      phase: 'Phase 4 — Contention',
      description: 'Aligneur de contention nocturne à vie.',
      duration: 'Indéfinie',
      status: 'upcoming',
    },
  ],
  [
    {
      id: 'd1',
      phase: 'Phase 1 — Quad helix',
      description:
        'Expansion lente sur quad helix collé. Activation tous les 15 jours.',
      duration: '5 mois',
      status: 'done',
      startDate: '2023-09-01',
      endDate: '2024-02-01',
    },
    {
      id: 'd2',
      phase: 'Phase 2 — Multi-bagues',
      description:
        'Arcs Niti 0.14 → 0.16 → 0.16×0.22 SS. Élastiques de finition légers.',
      duration: '16 mois',
      status: 'current',
      startDate: '2024-02-15',
    },
    {
      id: 'd3',
      phase: 'Phase 3 — Contention',
      description: 'Plaque amovible nocturne + fil collé 3-3 mandibulaire.',
      duration: 'Indéfinie',
      status: 'upcoming',
    },
  ],
  [
    {
      id: 'e1',
      phase: 'Phase 1 — Préparation',
      description:
        'Détartrage complet, polissage, motivation hygiène avant collage.',
      duration: '1 mois',
      status: 'done',
      startDate: '2024-06-01',
      endDate: '2024-07-01',
    },
    {
      id: 'e2',
      phase: 'Phase 2 — Damon SL',
      description:
        'Brackets autoligaturants Damon Q3, arcs Cu-Niti séquentiels.',
      duration: '20 mois',
      status: 'current',
      startDate: '2024-07-10',
    },
    {
      id: 'e3',
      phase: 'Phase 3 — Détails occlusaux',
      description: 'Coordination des arcades, élastiques verticaux antérieurs.',
      duration: '4 mois',
      status: 'upcoming',
    },
    {
      id: 'e4',
      phase: 'Phase 4 — Contention',
      description: 'Gouttière thermoformée bimaxillaire nocturne.',
      duration: 'Indéfinie',
      status: 'upcoming',
    },
  ],
];

export function treatmentPlanForPatient(uuid: string): TreatmentStep[] {
  return TREATMENT_PLAN_VARIANTS[
    pickIndices(uuid).plan % TREATMENT_PLAN_VARIANTS.length
  ]!;
}

export interface ObjectiveSnippet {
  id: string;
  category: 'dentaire' | 'squelettique' | 'esthetique' | 'fonctionnel';
  label: string;
  detail?: string;
  progress: number;
  status: 'pending' | 'active' | 'achieved';
  priority: 'haute' | 'normale' | 'basse';
}

const OBJECTIVES_VARIANTS: ObjectiveSnippet[][] = [
  [
    {
      id: 'o1',
      category: 'squelettique',
      label: 'Correction Classe II',
      detail: 'Améliorer le rapport squelettique ANB < 4°',
      progress: 55,
      status: 'active',
      priority: 'haute',
    },
    {
      id: 'o2',
      category: 'dentaire',
      label: 'Fermeture des espaces',
      detail: 'Combler les diastèmes résiduels post-extractions',
      progress: 70,
      status: 'active',
      priority: 'haute',
    },
    {
      id: 'o3',
      category: 'dentaire',
      label: 'Alignement incisif',
      detail: 'Niveler la courbe de Spee et aligner les axes dentaires',
      progress: 90,
      status: 'active',
      priority: 'normale',
    },
    {
      id: 'o4',
      category: 'esthetique',
      label: 'Exposition gingivale',
      detail: 'Réduire le sourire gingival à < 2 mm',
      progress: 40,
      status: 'active',
      priority: 'normale',
    },
    {
      id: 'o5',
      category: 'fonctionnel',
      label: 'Guide antérieur fonctionnel',
      detail: 'Établir un désengrenage canin et incisif correct',
      progress: 30,
      status: 'active',
      priority: 'haute',
    },
    {
      id: 'o6',
      category: 'dentaire',
      label: 'Correction du surplomb',
      detail: "Réduire l'overjet à 2–3 mm",
      progress: 100,
      status: 'achieved',
      priority: 'haute',
    },
    {
      id: 'o7',
      category: 'esthetique',
      label: 'Symétrie faciale',
      detail: 'Corriger la déviation du milieu dentaire',
      progress: 15,
      status: 'pending',
      priority: 'basse',
    },
  ],
  [
    {
      id: 'a1',
      category: 'dentaire',
      label: 'Désencombrement maxillaire',
      detail: 'Récupération de 7 mm via extractions et alignement',
      progress: 45,
      status: 'active',
      priority: 'haute',
    },
    {
      id: 'a2',
      category: 'dentaire',
      label: 'Désencombrement mandibulaire',
      detail: 'Récupération de 6 mm, contrôle du torque incisif',
      progress: 35,
      status: 'active',
      priority: 'haute',
    },
    {
      id: 'a3',
      category: 'esthetique',
      label: 'Profil facial',
      detail: 'Préserver le soutien labial après fermeture des espaces',
      progress: 60,
      status: 'active',
      priority: 'normale',
    },
    {
      id: 'a4',
      category: 'fonctionnel',
      label: 'Calage occlusal final',
      detail: 'Intercuspidation maximale stable',
      progress: 10,
      status: 'pending',
      priority: 'normale',
    },
  ],
  [
    {
      id: 'b1',
      category: 'squelettique',
      label: 'Avancée maxillaire',
      detail: 'Stimuler la croissance antérieure maxillaire',
      progress: 25,
      status: 'active',
      priority: 'haute',
    },
    {
      id: 'b2',
      category: 'squelettique',
      label: 'Expansion transversale',
      detail: 'Coordonner les arcades maxillaire/mandibulaire',
      progress: 80,
      status: 'active',
      priority: 'haute',
    },
    {
      id: 'b3',
      category: 'dentaire',
      label: 'Correction inversé antérieur',
      detail: 'Obtenir un surplomb positif de 2 mm',
      progress: 50,
      status: 'active',
      priority: 'haute',
    },
    {
      id: 'b4',
      category: 'fonctionnel',
      label: 'Élimination latérodéviation',
      detail: 'Trajet de fermeture centré',
      progress: 65,
      status: 'active',
      priority: 'normale',
    },
  ],
  [
    {
      id: 'c1',
      category: 'fonctionnel',
      label: 'Posture linguale',
      detail: 'Position de repos lingual au palais',
      progress: 75,
      status: 'active',
      priority: 'haute',
    },
    {
      id: 'c2',
      category: 'dentaire',
      label: 'Fermeture de la béance',
      detail: 'Recouvrement antérieur ≥ 2 mm',
      progress: 40,
      status: 'active',
      priority: 'haute',
    },
    {
      id: 'c3',
      category: 'esthetique',
      label: 'Ligne du sourire',
      detail: 'Harmonisation du plan incisif avec la lèvre supérieure',
      progress: 55,
      status: 'active',
      priority: 'normale',
    },
    {
      id: 'c4',
      category: 'squelettique',
      label: 'Contrôle vertical',
      detail: "Éviter l'extrusion molaire pendant la fermeture",
      progress: 30,
      status: 'active',
      priority: 'haute',
    },
  ],
  [
    {
      id: 'd1',
      category: 'esthetique',
      label: 'Sourire en arc',
      detail: 'Reproduire la courbure naturelle de la lèvre supérieure',
      progress: 85,
      status: 'active',
      priority: 'normale',
    },
    {
      id: 'd2',
      category: 'dentaire',
      label: 'Correction des rotations',
      detail: 'Dérotation prémolaires + canines maxillaires',
      progress: 50,
      status: 'active',
      priority: 'haute',
    },
    {
      id: 'd3',
      category: 'fonctionnel',
      label: 'Coordination occlusale',
      detail: 'Intercuspidation maximale stable et reproductible',
      progress: 35,
      status: 'active',
      priority: 'normale',
    },
  ],
  [
    {
      id: 'e1',
      category: 'squelettique',
      label: 'Avance mandibulaire',
      detail: 'Stimulation de la croissance via activateur',
      progress: 70,
      status: 'active',
      priority: 'haute',
    },
    {
      id: 'e2',
      category: 'dentaire',
      label: 'Correction de la surplomb',
      detail: 'Cible : overjet 2 mm',
      progress: 60,
      status: 'active',
      priority: 'haute',
    },
    {
      id: 'e3',
      category: 'esthetique',
      label: 'Harmonie du profil',
      detail: 'Réduction de la convexité faciale',
      progress: 45,
      status: 'active',
      priority: 'normale',
    },
    {
      id: 'e4',
      category: 'fonctionnel',
      label: 'Désengrenage canin',
      detail: "Établissement d'une fonction canine bilatérale",
      progress: 80,
      status: 'active',
      priority: 'normale',
    },
    {
      id: 'e5',
      category: 'dentaire',
      label: 'Alignement antérieur',
      detail: 'Six incisives parfaitement alignées',
      progress: 100,
      status: 'achieved',
      priority: 'normale',
    },
  ],
];

export function objectivesForPatient(uuid: string): ObjectiveSnippet[] {
  return OBJECTIVES_VARIANTS[
    pickIndices(uuid).obj % OBJECTIVES_VARIANTS.length
  ]!;
}

const COMMENTS_VARIANTS: ClinicalComment[][] = [
  CLINICAL_COMMENTS,
  [
    {
      id: 'a1',
      date: '2025-01-08',
      author: 'Dr. Moreau',
      content:
        'Coopération exemplaire. Hygiène impeccable malgré la complexité du dispositif. Activations conformes.',
      type: 'note',
    },
    {
      id: 'a2',
      date: '2024-12-12',
      author: 'Dr. Moreau',
      content:
        "Décollement d'un attachement 23 lors du repas. Recollage immédiat. Patient prévenu sur les aliments durs.",
      type: 'observation',
    },
    {
      id: 'a3',
      date: '2024-11-04',
      author: 'Dr. Moreau',
      content:
        'Anesthésie locale : éviter les vasoconstricteurs (antécédents cardiovasculaires).',
      type: 'alert',
    },
  ],
  [
    {
      id: 'b1',
      date: '2024-12-22',
      author: 'Dr. Moreau',
      content:
        "Bonne progression de la disjonction. Diastème inter-incisif central de 3 mm conforme à l'objectif.",
      type: 'note',
    },
    {
      id: 'b2',
      date: '2024-11-18',
      author: 'Dr. Moreau',
      content:
        'Port de masque de Delaire insuffisant selon journal de bord (< 10 h/jour). Rappel motivationnel.',
      type: 'observation',
    },
  ],
  [
    {
      id: 'c1',
      date: '2025-01-15',
      author: 'Dr. Moreau',
      content:
        "Refinement n°2 en place. Patient très observant des changements d'aligneurs aux 7 jours.",
      type: 'note',
    },
    {
      id: 'c2',
      date: '2024-12-02',
      author: 'Dr. Moreau',
      content:
        "Persistance d'une légère interposition linguale au repos. Reprise des séances d'orthophonie recommandée.",
      type: 'observation',
    },
    {
      id: 'c3',
      date: '2024-10-15',
      author: 'Dr. Moreau',
      content:
        "Patient sous traitement par bisphosphonates : précautions chirurgicales — pas d'extraction sans avis du médecin référent.",
      type: 'alert',
    },
  ],
  [
    {
      id: 'd1',
      date: '2025-02-04',
      author: 'Dr. Moreau',
      content:
        'Coopération mitigée sur le port des élastiques. Reprise du journal de bord lors de la prochaine séance.',
      type: 'observation',
    },
    {
      id: 'd2',
      date: '2024-11-28',
      author: 'Dr. Moreau',
      content:
        'Bonne dynamique éruptive des canines maxillaires. Préparation espace OK.',
      type: 'note',
    },
  ],
  [
    {
      id: 'e1',
      date: '2025-01-22',
      author: 'Dr. Moreau',
      content:
        'Hygiène irréprochable, indice de plaque < 10 %. Aucun saignement au sondage.',
      type: 'note',
    },
    {
      id: 'e2',
      date: '2024-12-10',
      author: 'Dr. Moreau',
      content:
        'Décollement répété du bracket 24. Recollage avec primer renforcé.',
      type: 'observation',
    },
    {
      id: 'e3',
      date: '2024-09-03',
      author: 'Dr. Moreau',
      content:
        "ATTENTION : patient porteur d'un pacemaker — éviter tout instrument piezo électrique pendant les soins.",
      type: 'alert',
    },
    {
      id: 'e4',
      date: '2024-06-20',
      author: 'Dr. Moreau',
      content:
        'Pose de séparateurs en vue du collage des bagues molaires. Bonne tolérance.',
      type: 'note',
    },
  ],
];

export function clinicalCommentsForPatient(uuid: string): ClinicalComment[] {
  return COMMENTS_VARIANTS[pickIndices(uuid).com % COMMENTS_VARIANTS.length]!;
}
